import { Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MAX_IMAGE_EDGE, MediaRejectedError, uploadImage, type StorageFolderType } from '@/data/storageUpload';

interface HeroImageFieldProps {
  value: string;
  onChange: (url: string) => void;
  /** Which Storage folder to upload into. See `StorageFolderType`. */
  folder?: StorageFolderType;
}

/** Single-image upload control, backed by Firebase Storage. */
export function HeroImageField({ value, onChange, folder = 'experiences' }: HeroImageFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const url = await uploadImage(file, folder);
      onChange(url);
    } catch (e) {
      // Same split as HeroVideoField: a file refused before it left the browser
      // says what to fix, so it is shown verbatim; a Firebase StorageError does
      // not, so it isn't.
      setError(e instanceof MediaRejectedError ? e.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      {value ? (
        <div className='tw:relative tw:inline-block'>
          <img src={value} alt='' className='tw:h-40 tw:w-auto tw:rounded-md tw:border tw:object-cover' />
          <button
            type='button'
            onClick={() => onChange('')}
            className='tw:absolute tw:-top-2 tw:-right-2 tw:rounded-full tw:bg-destructive tw:p-1 tw:text-destructive-foreground'
          >
            <X className='tw:size-3.5' />
          </button>
        </div>
      ) : (
        <Button
          type='button'
          variant='outline'
          onClick={() => inputRef.current?.click()}
          loading={uploading}
        >
          <Upload />
          {uploading ? 'Uploading…' : 'Upload Image'}
        </Button>
      )}

      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        className='tw:hidden'
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) handleFile(file);
        }}
      />

      <p className='tw:mt-1.5 tw:text-xs tw:text-muted-foreground'>
        Resized to {MAX_IMAGE_EDGE}px on its longest edge and saved as WebP, so a photo straight off a phone is fine to
        upload.
      </p>

      {error && <p className='tw:mt-1.5 tw:text-sm tw:text-destructive'>{error}</p>}
    </div>
  );
}
